import { Controller } from "@hotwired/stimulus"
import { responseIsCurrent } from "../lib/request_freshness"
import { WEB_EVENTS } from "../lib/web_events"
import { esc } from "../lib/html"

// Map search bar: debounced typeahead against /map/search. Picking a result
// dispatches the shared flyTo event so the map controller recenters there.
export default class extends Controller {
  static targets = ["input", "results"]
  static values = { url: String }

  connect() {
    this.timer = null
    this.requestId = 0
    this.abortController = null
    this.results = []
    this.active = -1
    this.onDocClick = e => { if (!this.element.contains(e.target)) this.#hide() }
    document.addEventListener("click", this.onDocClick)
  }

  disconnect() {
    document.removeEventListener("click", this.onDocClick)
    clearTimeout(this.timer)
    this.#cancelFetch()
  }

  input() {
    clearTimeout(this.timer)
    this.requestId += 1
    this.#cancelFetch()
    const q = this.inputTarget.value.trim()
    if (q.length < 2) return this.#hide()
    const requestId = this.requestId
    this.timer = setTimeout(() => this.#fetch(q, requestId), 200)
  }

  keydown(e) {
    if (this.resultsTarget.classList.contains("hidden") || !this.results.length) return
    if (e.key === "ArrowDown") { e.preventDefault(); this.#move(1) }
    else if (e.key === "ArrowUp") { e.preventDefault(); this.#move(-1) }
    else if (e.key === "Enter") { e.preventDefault(); this.#pick(this.results[this.active] || this.results[0]) }
    else if (e.key === "Escape") { this.#hide() }
  }

  async #fetch(q, requestId) {
    const abortController = new AbortController()
    this.abortController = abortController
    try {
      const res = await fetch(`${this.urlValue}?q=${encodeURIComponent(q)}`, {
        headers: { Accept: "application/json" },
        signal: abortController.signal
      })
      const data = await res.json()
      if (!responseIsCurrent({ requestId, latestRequestId: this.requestId })) return
      this.results = data.results || []
      this.active = -1
      this.#render()
    } catch (e) {
      if (e?.name === "AbortError") return
      if (responseIsCurrent({ requestId, latestRequestId: this.requestId })) this.#hide()
    } finally {
      if (this.abortController === abortController) this.abortController = null
    }
  }

  #cancelFetch() {
    this.abortController?.abort()
    this.abortController = null
  }

  #render() {
    if (!this.results.length) {
      this.resultsTarget.innerHTML = `<div class="px-3 py-2 text-sm text-gray-400">No matches</div>`
      return this.#show()
    }
    this.resultsTarget.innerHTML = this.results.map((r, i) => `
      <button type="button" data-index="${i}" class="search-row flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-gray-100">
        <span class="truncate"><span class="font-medium text-gray-900">${esc(r.name)}</span>${r.state ? ` <span class="text-gray-400">${esc(r.state)}</span>` : ""}</span>
        <span class="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500">${esc(r.kind)}</span>
      </button>`).join("")
    this.resultsTarget.querySelectorAll("button").forEach(b =>
      b.addEventListener("click", () => this.#pick(this.results[Number(b.dataset.index)]))
    )
    this.#show()
  }

  #move(delta) {
    const rows = this.resultsTarget.querySelectorAll(".search-row")
    this.active = (this.active + delta + rows.length) % rows.length
    rows.forEach((row, i) => row.classList.toggle("bg-gray-100", i === this.active))
  }

  #pick(r) {
    if (!r) return
    window.dispatchEvent(new CustomEvent(WEB_EVENTS.flyTo, {
      detail: { lat: Number(r.lat), lon: Number(r.lon), zoom: Number(r.zoom) || 12 }
    }))
    this.inputTarget.value = r.name
    this.inputTarget.blur()
    this.#hide()
  }

  #show() { this.resultsTarget.classList.remove("hidden") }
  #hide() {
    this.requestId += 1
    this.#cancelFetch()
    this.resultsTarget.classList.add("hidden")
    this.resultsTarget.innerHTML = ""
    this.results = []
    this.active = -1
  }
}
